/**
 * HelpPanel – slide-out help drawer from the right side.
 * Open with `?` or `F1` (when not typing in an input), or via the floating "?" button.
 * Close with Escape, clicking the backdrop, or the close button.
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import {
  X, HelpCircle, Search, ChevronDown, ChevronRight,
  BookOpen, Terminal as TerminalIcon, Bot, Users, TrendingUp,
  Database, BarChart2, LayoutGrid, Settings, Keyboard, Zap,
} from 'lucide-react'
import { clsx } from 'clsx'

// ── Reusable sub-components ───────────────────────────────────────────────────

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-text-primary mb-1.5">{title}</h3>
      <div className="text-xs text-text-secondary leading-relaxed space-y-1.5">{children}</div>
    </div>
  )
}

function PanelSection({
  id, title, icon: Icon, children, defaultOpen = false,
}: {
  id: string; title: string; icon: React.ElementType; children: React.ReactNode; defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div id={id} className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-surface-hover/50 transition-colors bg-surface-DEFAULT"
        aria-expanded={open}
      >
        <Icon size={14} className="text-blue-light shrink-0" aria-hidden />
        <span className="flex-1 font-medium text-text-primary text-xs">{title}</span>
        {open
          ? <ChevronDown size={13} className="text-text-muted shrink-0" />
          : <ChevronRight size={13} className="text-text-muted shrink-0" />
        }
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 space-y-3 border-t border-border bg-base/50">
          {children}
        </div>
      )}
    </div>
  )
}

// ── Compact section content ───────────────────────────────────────────────────

function GettingStarted() {
  return (
    <>
      <Sub title="Quick start">
        <p>Open the <strong>Terminal</strong> and type a command like <code className="font-mono text-blue-light">/quote AAPL</code> to get started.</p>
        <p>Use <kbd className="px-1 py-0.5 rounded bg-surface-DEFAULT border border-border font-mono text-[10px]">Ctrl+K</kbd> to open the command palette for quick navigation.</p>
      </Sub>
      <Sub title="Key pages">
        <ul className="list-disc list-inside space-y-0.5">
          <li><strong>Terminal</strong> — all commands live here</li>
          <li><strong>Charts</strong> — interactive technical charts</li>
          <li><strong>Portfolio</strong> — your holdings and P&L</li>
          <li><strong>Recommendations</strong> — agent trade ideas</li>
          <li><strong>Intelligence</strong> — alternative data</li>
        </ul>
      </Sub>
    </>
  )
}

function TerminalCommands({ search }: { search: string }) {
  const categories = [
    { title: 'Equity', cmds: ['/quote AAPL', '/chart AAPL 1y', '/profile AAPL', '/earnings AAPL', '/technicals AAPL', '/compare AAPL MSFT'] },
    { title: 'Market', cmds: ['/indices', '/movers', '/sectors', '/bonds', '/vix', '/forex', '/economic'] },
    { title: 'Portfolio', cmds: ['/portfolio', '/positions', '/risk', '/pnl', '/pot', '/goals', '/watchlist'] },
    { title: 'Agents', cmds: ['/analyze', '/recommendations', '/consensus AAPL'] },
    { title: 'Alt Data', cmds: ['/congress', '/insider AAPL', '/guru Buffett', '/sentiment AAPL', '/flows', '/fear'] },
    { title: 'Crypto', cmds: ['/crypto', '/crypto top', '/crypto dashboard', '/crypto detail BTC'] },
  ]
  const lc = search.toLowerCase()
  const filtered = lc
    ? categories.map(c => ({ ...c, cmds: c.cmds.filter(cmd => cmd.toLowerCase().includes(lc)) })).filter(c => c.cmds.length > 0)
    : categories
  return (
    <div className="space-y-2">
      {filtered.map(cat => (
        <div key={cat.title}>
          <p className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-1">{cat.title}</p>
          <div className="space-y-0.5">
            {cat.cmds.map(cmd => (
              <div key={cmd} className="font-mono text-[11px] text-blue-light bg-base rounded px-2 py-1">{cmd}</div>
            ))}
          </div>
        </div>
      ))}
      {filtered.length === 0 && <p className="text-xs text-text-muted">No commands match "{search}"</p>}
    </div>
  )
}

function AgentsContent() {
  return (
    <>
      <Sub title="7 AI agents">
        <div className="space-y-1.5">
          {[
            { name: 'ARIA', role: 'Chat assistant — ask anything' },
            { name: 'Market', role: 'Regime detection + macro signals' },
            { name: 'Portfolio', role: 'Allocation + fundamentals' },
            { name: 'Risk', role: 'VaR, drawdown, pot limits' },
            { name: 'Compliance', role: 'FCA / MiFID II gate' },
            { name: 'Goals', role: 'Monte Carlo goal tracking' },
            { name: 'Screener', role: 'High-volume ticker scan' },
          ].map(a => (
            <div key={a.name} className="flex items-start gap-2 bg-surface-DEFAULT rounded px-2 py-1.5 border border-border">
              <Bot size={11} className="text-blue-light mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold text-text-primary text-[11px]">{a.name}</span>
                <span className="text-text-muted text-[11px]"> — {a.role}</span>
              </div>
            </div>
          ))}
        </div>
      </Sub>
      <Sub title="Run analysis">
        <p>Type <code className="font-mono text-blue-light">/analyze</code> or click "Run Analysis" on the AI Agents page.</p>
      </Sub>
    </>
  )
}

function KeyboardShortcuts() {
  const shortcuts = [
    { keys: ['?', 'F1'], desc: 'Open this help panel' },
    { keys: ['Esc'], desc: 'Close panels and modals' },
    { keys: ['Ctrl+K'], desc: 'Command palette' },
    { keys: ['↑', '↓'], desc: 'Terminal history' },
    { keys: ['Tab'], desc: 'Autocomplete in terminal' },
    { keys: ['Ctrl+L'], desc: 'Clear terminal' },
  ]
  return (
    <div className="space-y-1.5">
      {shortcuts.map(s => (
        <div key={s.desc} className="flex items-center gap-2">
          <div className="flex gap-1 shrink-0">
            {s.keys.map(k => (
              <kbd key={k} className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-surface-DEFAULT border border-border text-text-secondary">{k}</kbd>
            ))}
          </div>
          <span className="text-xs text-text-secondary">{s.desc}</span>
        </div>
      ))}
    </div>
  )
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface HelpPanelProps {
  open: boolean
  onClose: () => void
}

export function HelpFloatingButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 w-10 h-10 rounded-full bg-blue text-white
                 flex items-center justify-center shadow-lg hover:bg-blue/80 transition-colors
                 focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 focus-visible:ring-offset-base"
      aria-label="Open help panel (? or F1)"
      title="Help (? or F1)"
    >
      <HelpCircle size={18} aria-hidden />
    </button>
  )
}

export default function HelpPanel({ open, onClose }: HelpPanelProps) {
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Focus search on open
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50)
    } else {
      setSearch('')
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const lc = search.toLowerCase()

  const sections = useMemo(() => [
    { id: 'gs', title: 'Getting Started', icon: Zap, content: <GettingStarted />, defaultOpen: true },
    { id: 'cmds', title: 'Terminal Commands', icon: TerminalIcon, content: <TerminalCommands search={search} /> },
    { id: 'agents', title: 'AI Agents', icon: Bot, content: <AgentsContent /> },
    { id: 'kb', title: 'Keyboard Shortcuts', icon: Keyboard, content: <KeyboardShortcuts /> },
  ], [search])

  const filteredSections = useMemo(() => {
    if (!lc) return sections
    return sections.filter(s =>
      s.title.toLowerCase().includes(lc) || s.id === 'cmds'
    )
  }, [lc, sections])

  if (!open) return null

  return (
    <>
      {/* Non-modal panel — no backdrop, side menu stays clickable */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-label="Help"
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[400px] bg-surface-DEFAULT border-l border-border
                   flex flex-col shadow-2xl animate-slide-in focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen size={15} className="text-blue-light" aria-hidden />
            <h2 className="font-semibold text-text-primary text-sm">Help</h2>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/app/help"
              className="text-[11px] text-blue-light hover:underline"
              onClick={onClose}
            >
              Full docs →
            </a>
            <button
              onClick={onClose}
              className="btn-ghost p-1.5"
              aria-label="Close help panel"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-2.5 border-b border-border shrink-0">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search help..."
              className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-base border border-border
                         text-xs text-text-primary placeholder:text-text-muted
                         focus:outline-none focus:border-blue"
            />
          </div>
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {filteredSections.map(s => (
            <PanelSection key={s.id} id={s.id} title={s.title} icon={s.icon} defaultOpen={s.defaultOpen}>
              {s.content}
            </PanelSection>
          ))}

          {/* Footer hint */}
          <p className="text-center text-[10px] text-text-muted pt-2 pb-1">
            Press <kbd className="px-1 py-0.5 rounded bg-surface-DEFAULT border border-border font-mono text-[10px]">?</kbd> or <kbd className="px-1 py-0.5 rounded bg-surface-DEFAULT border border-border font-mono text-[10px]">F1</kbd> to toggle
          </p>
        </div>
      </div>
    </>
  )
}
